import uPlot from "uplot";

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";
import { cloneTemplate, fillSlots } from "../utils/template.js";

const STATUS_LABELS = {
  sous_basique: "sous franchise basique",
  entre_basique_et_majore_annee_1: "1ʳᵉ année de dépassement basique",
  entre_basique_et_majore_annee_2: "2ᵉ année consécutive de dépassement basique",
  au_dessus_majore: "au-dessus du seuil majoré (assujetti)",
};

const MONTH_LABELS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export default class SeuilsDashboardController extends BaseController {
  static targets = [
    "errorBanner", "errorMessage", "intracomBtn",
    "overline",
    "prevYear1Label", "prevYear1Ca", "prevYear2Label", "prevYear2Ca",
    "caCumulatedValue", "caCumulatedDelta",
    "projectionValue", "projectionDelta",
    "marginValue", "marginDelta",
    "marginMicroValue", "marginMicroDelta",
    "vigueurBlocks",
    "chart", "chartCard",
    "alert", "alertText", "alertSub",
    "franchiseLabel", "franchiseAmount", "franchiseFill", "franchiseFoot",
    "majoredLabel", "majoredAmount", "majoredFill", "majoredFoot",
    "microcapLabel", "microcapAmount", "microcapFill", "microcapFoot",
    "historyTbody", "historyCard",
  ];

  async connect() {
    try {
      const [thresholds, status, history] = await Promise.all([
        api.get("/me/vat-thresholds"),
        api.get("/me/vat-status"),
        api.get("/me/vat-thresholds/history", { months: "24" }),
      ]);
      this._renderStatus(status);
      this._renderThresholds(thresholds, status);
      this._renderHistory(history);
      this._renderChart(history, thresholds);
      this._renderPriorYears();
    } catch (error) {
      const message = error instanceof APIError
        ? error.message
        : "Impossible de charger les données des seuils.";
      this._showErrorBanner(message);
    }
  }

  _renderStatus(status) {
    const showIntracom = status.vatTaxable === true
      && status.vatIntracomStatus !== "received";
    if (this.hasIntracomBtnTarget) {
      this.intracomBtnTarget.hidden = !showIntracom;
    }
  }

  _renderThresholds(thresholds, status) {
    const primary = this._pickPrimaryCategory(thresholds.categories, status.categories);
    if (!primary) {
      this._showErrorBanner("Aucune catégorie fiscale active. Configurez une activité avant de suivre vos seuils.");
      return;
    }

    if (this.hasOverlineTarget) {
      this.overlineTarget.textContent =
        `Année civile ${new Date().getFullYear()} · ${this._categoryLabel(primary.category)}`;
    }

    this._fillStats(primary, thresholds.mixedTotal ?? null);
    this._fillFranchiseGauge(primary);
    this._fillMajoredGauge(primary);
    this._fillMicroCapGauge(thresholds.mixedTotal ?? null, primary);
    this._fillProjectionAlert(primary, status);
    this._renderVigueurBlocks(thresholds.categories);
  }

  _pickPrimaryCategory(thresholdsCategories, statusCategories) {
    if (!Array.isArray(thresholdsCategories) || thresholdsCategories.length === 0) {
      return null;
    }
    const ranked = [...thresholdsCategories].sort((a, b) => {
      const pctA = this._pctOfBasic(a);
      const pctB = this._pctOfBasic(b);
      return pctB - pctA;
    });
    const top = ranked[0];
    const matchingStatus = statusCategories?.find((c) => c.category === top.category);
    return { ...top, status: matchingStatus?.status ?? null };
  }

  _fillStats(primary, mixedTotal) {
    this.caCumulatedValueTarget.textContent = this._formatEurShort(primary.caYtd);
    this.caCumulatedDeltaTarget.textContent = `Catégorie ${this._categoryLabel(primary.category)}`;

    this.projectionValueTarget.textContent = this._formatEurShort(primary.projectionLinear);
    this.projectionDeltaTarget.textContent = primary.projectionTrend > 0
      ? `Tendance glissante : ${this._formatEurShort(primary.projectionTrend)}`
      : "Tendance glissante indisponible";

    this.marginValueTarget.textContent = this._formatEurShort(primary.marginBasicEur);
    this.marginDeltaTarget.textContent = primary.marginBasicEur > 0
      ? `${this._formatPct(primary.marginBasicPct)} restants`
      : "Seuil franchi";

    this._fillMarginMicroStat(primary, mixedTotal);
  }

  _fillMarginMicroStat(primary, mixedTotal) {
    const useMixed = mixedTotal !== null && mixedTotal !== undefined;
    const marginEur = useMixed ? mixedTotal.marginEur : primary.marginMicroEur;
    const marginPct = useMixed ? mixedTotal.marginPct : primary.marginMicroPct;

    this.marginMicroValueTarget.textContent = this._formatEurShort(marginEur);
    this.marginMicroDeltaTarget.textContent = marginEur > 0
      ? `${this._formatPct(marginPct)} restants${useMixed ? " (activité mixte)" : ""}`
      : "Plafond franchi";
  }

  _fillFranchiseGauge(primary) {
    const pct = this._pctOfBasic(primary);
    this.franchiseLabelTarget.textContent = "Franchise en base de TVA";
    this.franchiseAmountTarget.textContent = `${this._formatEurShort(primary.caYtd)} / ${this._formatEurShort(primary.thresholdBasic)}`;
    this._applyFill(this.franchiseFillTarget, pct);
    this.franchiseFootTarget.textContent = `${this._formatPct(pct)} atteint · marge ${this._formatEurShort(primary.marginBasicEur)}`;
  }

  _fillMajoredGauge(primary) {
    const pct = this._pctOfMajored(primary);
    this.majoredLabelTarget.textContent = "Seuil majoré TVA (effet immédiat)";
    this.majoredAmountTarget.textContent = `${this._formatEurShort(primary.caYtd)} / ${this._formatEurShort(primary.thresholdMajored)}`;
    this._applyFill(this.majoredFillTarget, pct);
    this.majoredFootTarget.textContent = `${this._formatPct(pct)} atteint · franchissement = TVA exigible dès la facture-déclencheur (incluse)`;
  }

  _fillMicroCapGauge(mixedTotal, primary) {
    const useMixed = mixedTotal !== null && mixedTotal !== undefined;
    const ca = useMixed ? mixedTotal.caYtd : primary.caYtd;
    const threshold = useMixed ? mixedTotal.thresholdMicroExit : (primary.thresholdMicroExit ?? 0);
    const pct = threshold > 0 ? Math.min(100, (ca / threshold) * 100) : 0;

    this.microcapLabelTarget.textContent = useMixed
      ? "Plafond régime micro (activité mixte)"
      : `Plafond régime micro (${this._categoryLabel(primary.category)})`;
    this.microcapAmountTarget.textContent = threshold > 0
      ? `${this._formatEurShort(ca)} / ${this._formatEurShort(threshold)}`
      : `${this._formatEurShort(ca)} / —`;
    this._applyFill(this.microcapFillTarget, pct);
    this.microcapFootTarget.textContent = threshold > 0
      ? `${this._formatPct(pct)} atteint · sortie automatique du régime micro au-delà`
      : "Plafond non applicable";
  }

  _fillProjectionAlert(primary, status) {
    if (!this.hasAlertTarget) return;

    const statusValue = primary.status;
    const isAtRisk = statusValue === "entre_basique_et_majore_annee_1"
      || statusValue === "entre_basique_et_majore_annee_2"
      || statusValue === "au_dessus_majore";

    if (!isAtRisk) {
      this.alertTarget.hidden = true;
      return;
    }

    const label = STATUS_LABELS[statusValue] ?? "à surveiller";
    this.alertTextTarget.textContent = `"Catégorie ${this._categoryLabel(primary.category)} — ${label}."`;
    this.alertSubTarget.textContent = status.vatTaxable
      ? "Votre organization est désormais assujettie à la TVA. Préparez la demande de n° intracom et le choix du régime réel."
      : "Notamicro vous notifiera à chaque palier et orchestrera la bascule en TVA si vous franchissez le seuil majoré.";
    this.alertTarget.hidden = false;
  }

  /**
   * Renseigne les exercices N-1 / N-2 du bloc « Règles N-1 / N-2 ». Les
   * libellés d'année sont dérivés localement ; le CA déclaré est le total HT
   * encaissé de l'exercice, lu depuis le livre des recettes
   * (`GET /revenue/register?year=N`, source canonique). Une année sans
   * recette enregistrée (ME non actif à l'époque) affiche une mention neutre.
   */
  _renderPriorYears() {
    const year = new Date().getFullYear();
    if (this.hasPrevYear1LabelTarget) {
      this.prevYear1LabelTarget.textContent = `Année N-1 (${year - 1})`;
    }
    if (this.hasPrevYear2LabelTarget) {
      this.prevYear2LabelTarget.textContent = `Année N-2 (${year - 2})`;
    }
    if (this.hasPrevYear1CaTarget) {
      this._loadPriorYearCa(year - 1, this.prevYear1CaTarget);
    }
    if (this.hasPrevYear2CaTarget) {
      this._loadPriorYearCa(year - 2, this.prevYear2CaTarget);
    }
  }

  async _loadPriorYearCa(year, target) {
    try {
      const data = await api.get("/revenue/register", { year: String(year) });
      const entriesCount = data?.totals?.entriesCount ?? 0;
      target.textContent = entriesCount > 0
        ? this._formatEurShort(data.totals.totalExclVat)
        : "Aucune recette enregistrée";
    } catch {
      target.textContent = "—";
    }
  }

  _renderHistory(history) {
    if (!this.hasHistoryTbodyTarget) return;

    const tbody = this.historyTbodyTarget;
    tbody.replaceChildren();

    const months = Array.isArray(history.months) ? history.months : [];
    if (months.length === 0) {
      this.historyCardTarget.hidden = true;
      return;
    }

    const cumulByYear = {};
    let basicThreshold = 0;
    let microThreshold = 0;

    for (const monthEntry of months) {
      const ymParts = monthEntry.month.split("-");
      const year = Number.parseInt(ymParts[0], 10);
      const monthIdx = Number.parseInt(ymParts[1], 10) - 1;
      const caMonth = monthEntry.categories.reduce((sum, c) => sum + (c.ca ?? 0), 0);
      cumulByYear[year] = (cumulByYear[year] ?? 0) + caMonth;

      basicThreshold = monthEntry.categories[0]?.thresholdBasic ?? basicThreshold;
      microThreshold = basicThreshold > 0 ? basicThreshold * 2 : microThreshold;

      const pctFranchise = basicThreshold > 0
        ? `${this._formatPct((cumulByYear[year] / basicThreshold) * 100)}`
        : "—";
      const pctMicro = microThreshold > 0
        ? `${this._formatPct((cumulByYear[year] / microThreshold) * 100)}`
        : "—";

      const fragment = cloneTemplate("vat-history-row-template");
      fillSlots(fragment, {
        month: `${MONTH_LABELS[monthIdx] ?? ""} ${year}`,
        caMonth: this._formatEurAmount(caMonth),
        caYearCumul: this._formatEurAmount(cumulByYear[year]),
        pctFranchise,
        pctMicro,
      });
      tbody.appendChild(fragment);
    }
  }

  /**
   * Trace l'histogramme du CA mensuel de l'année civile en cours, la courbe
   * de cumul, la projection annuelle (pointillé) et les lignes de seuils
   * (franchise + majoré) de la catégorie primaire, via uPlot.
   *
   * Le graphe n'est qu'une aide visuelle : la table d'historique en dessous
   * reste l'équivalent accessible (le conteneur porte `role="img"`).
   */
  _renderChart(history, thresholds) {
    if (!this.hasChartTarget) return;

    const primary = this._pickPrimaryCategory(thresholds.categories, null);
    if (!primary) return;

    const container = this.chartTarget;
    const width = container.clientWidth;
    // Pas de layout (jsdom en test, conteneur masqué) → on n'instancie pas uPlot.
    if (!width) return;

    const year = new Date().getFullYear();
    const now = new Date();
    const currentMonth = now.getFullYear() === year ? now.getMonth() + 1 : 12;

    const monthly = new Array(12).fill(0);
    const monthsArr = Array.isArray(history.months) ? history.months : [];
    let hasYearData = false;
    for (const entry of monthsArr) {
      const [y, mm] = entry.month.split("-").map(Number);
      if (y !== year) continue;
      hasYearData = true;
      monthly[mm - 1] = entry.categories.reduce((sum, c) => sum + (c.ca ?? 0), 0);
    }

    if (!hasYearData) {
      if (this.hasChartCardTarget) this.chartCardTarget.hidden = true;
      return;
    }

    const xs = [];
    const bars = [];
    const cumul = [];
    let run = 0;
    for (let i = 1; i <= 12; i++) {
      xs.push(i);
      if (i <= currentMonth) {
        bars.push(monthly[i - 1]);
        run += monthly[i - 1];
        cumul.push(run);
      } else {
        bars.push(null);
        cumul.push(null);
      }
    }

    // Projection : segment pointillé du cumul courant jusqu'au cumul projeté
    // en fin d'année (spanGaps relie les deux seuls points non nuls).
    const proj = new Array(12).fill(null);
    proj[currentMonth - 1] = cumul[currentMonth - 1] ?? 0;
    proj[11] = primary.projectionLinear;

    const franchise = new Array(12).fill(primary.thresholdBasic);
    const majored = new Array(12).fill(primary.thresholdMajored);

    const css = getComputedStyle(document.documentElement);
    const token = (name, fallback) => (css.getPropertyValue(name).trim() || fallback);
    const accent = token("--accent", "#4A3F5C");
    const ochre = token("--warning", "#B8A04A");
    const red = token("--error", "#762D38");
    const muted = token("--muted", "#6B675D");
    const border = token("--border", "#DDD8CE");

    const data = [xs, bars, cumul, proj, franchise, majored];
    const opts = {
      width,
      height: 260,
      padding: [12, 8, 4, 8],
      scales: { x: { time: false } },
      axes: [
        {
          values: (_u, splits) => splits.map((v) => MONTH_LABELS[v - 1]?.slice(0, 1) ?? ""),
          stroke: muted,
          grid: { show: false },
          ticks: { show: false },
        },
        {
          values: (_u, splits) => splits.map((v) => this._formatEurShort(v)),
          stroke: muted,
          grid: { stroke: border, width: 1 },
          ticks: { show: false },
        },
      ],
      series: [
        {},
        {
          label: "CA mensuel",
          stroke: accent,
          fill: this._withAlpha(accent, 0.18),
          paths: uPlot.paths.bars({ size: [0.5, 60] }),
          points: { show: false },
        },
        { label: "Cumul année", stroke: accent, width: 2, points: { show: false } },
        { label: "Projection", stroke: accent, width: 1, dash: [6, 4], spanGaps: true, points: { show: false } },
        { label: "Franchise TVA", stroke: ochre, width: 1, dash: [2, 4], points: { show: false } },
        { label: "Seuil majoré", stroke: red, width: 1, dash: [2, 4], points: { show: false } },
      ],
    };

    container.replaceChildren();
    this._chart = new uPlot(opts, data, container);

    if (!this._chartResizeBound) {
      this._chartResizeBound = true;
      window.addEventListener("resize", () => {
        if (this._chart && this.hasChartTarget) {
          this._chart.setSize({ width: this.chartTarget.clientWidth, height: 260 });
        }
      });
    }
  }

  _withAlpha(color, alpha) {
    const hex = color.replace("#", "");
    if (hex.length === 6) {
      const r = Number.parseInt(hex.slice(0, 2), 16);
      const g = Number.parseInt(hex.slice(2, 4), 16);
      const b = Number.parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color;
  }

  _pctOfBasic(category) {
    if (!category.thresholdBasic || category.thresholdBasic <= 0) return 0;
    return Math.min(200, (category.caYtd / category.thresholdBasic) * 100);
  }

  _pctOfMajored(category) {
    if (!category.thresholdMajored || category.thresholdMajored <= 0) return 0;
    return Math.min(200, (category.caYtd / category.thresholdMajored) * 100);
  }

  _applyFill(element, pct) {
    const clamped = Math.max(0, Math.min(100, pct));
    element.style.width = `${clamped}%`;
    element.classList.remove("is-warning", "is-error");
    if (pct >= 100) {
      element.classList.add("is-error");
    } else if (pct >= 80) {
      element.classList.add("is-warning");
    }
  }

  _renderVigueurBlocks(categories) {
    if (!this.hasVigueurBlocksTarget) return;

    const list = Array.isArray(categories) ? categories : [];
    if (list.length === 0) return;

    const year = new Date().getFullYear();
    const container = this.vigueurBlocksTarget;
    container.replaceChildren();

    for (const category of list) {
      const fragment = cloneTemplate("seuils-vigueur-block-template");
      fillSlots(fragment, {
        title: `Seuils ${year} — ${this._categoryLabel(category.category)}`,
        franchise: this._formatEurShort(category.thresholdBasic),
        majored: this._formatEurShort(category.thresholdMajored),
        microExit: this._formatEurShort(category.thresholdMicroExit),
      });
      container.appendChild(fragment);
    }
  }

  _categoryLabel(category) {
    switch (category) {
      case "bic_vente": return "BIC vente";
      case "bic_services": return "BIC services";
      case "bnc": return "BNC";
      default: return category ?? "—";
    }
  }

  _formatEurShort(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  }

  _formatEurAmount(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return `${n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }

  _formatPct(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return `${Math.round(n)} %`;
  }

  _showErrorBanner(message) {
    if (!this.hasErrorBannerTarget) return;
    if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = message;
    this.errorBannerTarget.hidden = false;
  }
}
